import React, { useCallback, useEffect } from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { Popover, Alignment } from '@prisjakt/ui/popover';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import { MobileOnly, MediumUp } from 'src/ui/block';
import { Truncated } from 'src/components/truncated';
import OptionsColumn from '../../row-types/alternative-price-row/options-cloumn';
import { ExpandButton } from '../expand-button';
import { GoToStoreButton } from '../go-to-store-button';
import { StockPriceInfo } from '../stock-price-info';
import { StoreInfoColumn } from '../store-info-column';
import { PriceInfoExperiment } from '../price-info-experiment';

import { AlternativePrice } from 'src/types';
import { ITableLinkProps } from '../../types';

import {
  StyledContent,
  StyledEndColumn,
  StyledExpandButton,
  StyledLink,
  StyledMiddleColumn,
  StyledProductName,
  StyledProductNameColumn,
  StyledRow,
  StyledTopMiddleRow,
} from './styles';

type Props = {
  alternativeOffer?: AlternativePrice;
  expanded?: boolean;
  handleExpandButtonClick?: (
    event: React.MouseEvent<Element, MouseEvent>
  ) => void;
  isAlternative?: boolean;
  priceRowRef?: React.RefObject<HTMLDivElement>;
  setShowDisabledInfo?: React.Dispatch<React.SetStateAction<boolean>>;
  showDisabledInfo?: boolean;
  tableLinkProps: ITableLinkProps;
};

export const UnfeaturedColumns: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<Props>
>> = props => {
  const {
    alternativeOffer,
    expanded = false,
    handleExpandButtonClick,
    isAlternative = false,
    priceRowRef,
    setShowDisabledInfo,
    showDisabledInfo,
    tableLinkProps,
  } = props;
  const { t } = useAppContext();
  const {
    newOfferItemExperiment,
    offer,
    forceMobileView,
  } = usePriceRowContext();

  const { priceId } = offer;

  const name = alternativeOffer?.name || offer.name;

  const handleOutSideClick = useCallback(
    (e: MouseEvent) => {
      if (
        priceRowRef &&
        priceRowRef.current &&
        priceRowRef.current.contains(e.target as HTMLElement)
      ) {
        return;
      }
      if (setShowDisabledInfo) setShowDisabledInfo(false);
    },
    [priceRowRef, setShowDisabledInfo]
  );

  // componentWillUnmount
  useEffect(() => {
    return () => {
      window.removeEventListener('click', handleOutSideClick);
    };
  }, [handleOutSideClick]);

  // Callback for showDisabledInfo state change
  useEffect(() => {
    if (!setShowDisabledInfo) return;
    if (showDisabledInfo) {
      window.addEventListener('click', handleOutSideClick);
    } else {
      window.removeEventListener('click', handleOutSideClick);
    }
  }, [handleOutSideClick, setShowDisabledInfo, showDisabledInfo]);

  const renderBaseline = () => {
    return (
      <StyledLink {...tableLinkProps} aria-describedby={`click-out-${priceId}`}>
        {!isAlternative && <StoreInfoColumn />}
        <StyledMiddleColumn>
          <StyledTopMiddleRow $forceMobileView={forceMobileView}>
            <StyledProductNameColumn
              $forceMobileView={Boolean(forceMobileView)}
            >
              <StyledProductName $forceMobileView={forceMobileView}>
                {name}
              </StyledProductName>
            </StyledProductNameColumn>
            <StockPriceInfo alternativeOffer={alternativeOffer} />
          </StyledTopMiddleRow>
        </StyledMiddleColumn>

        <StyledEndColumn $forceMobileView={forceMobileView}>
          <GoToStoreButton />
        </StyledEndColumn>
      </StyledLink>
    );
  };

  const renderExperiment = () => {
    return (
      <>
        <MobileOnly>
          <StyledContent>
            <StyledRow>
              <StyledLink
                {...tableLinkProps}
                aria-describedby={`click-out-${priceId}`}
                $newOfferItemExperiment={Boolean(newOfferItemExperiment)}
              >
                <StyledMiddleColumn
                  $newOfferItemExperiment={Boolean(newOfferItemExperiment)}
                >
                  <div>
                    <StyledProductNameColumn
                      $forceMobileView={Boolean(forceMobileView)}
                    >
                      <StyledProductName $forceMobileView={forceMobileView}>
                        {name}
                      </StyledProductName>
                    </StyledProductNameColumn>
                    <PriceInfoExperiment alternativeOffer={alternativeOffer} />
                  </div>
                </StyledMiddleColumn>

                {!isAlternative && <StoreInfoColumn />}
              </StyledLink>

              {!isAlternative && (
                <StyledExpandButton onClick={handleExpandButtonClick}>
                  <ExpandButton
                    expanded={expanded}
                    title={t('product', 'price.more-info')}
                  />
                </StyledExpandButton>
              )}
              {alternativeOffer && (
                <OptionsColumn alternativeOffer={alternativeOffer} />
              )}
            </StyledRow>

            {isAlternative && (
              <StyledProductNameColumn $newOfferItemExperiment>
                <Truncated>{name}</Truncated>
              </StyledProductNameColumn>
            )}
          </StyledContent>
        </MobileOnly>

        <MediumUp>{renderBaseline()}</MediumUp>
      </>
    );
  };

  return (
    <>
      {newOfferItemExperiment ? renderExperiment() : renderBaseline()}

      <Popover
        id={`click-out-${priceId}`}
        visible={Boolean(showDisabledInfo)}
        width={250}
        horizontalAlign={Alignment.Center}
        verticalAlign={Alignment.Center}
        onClose={() =>
          setShowDisabledInfo && setShowDisabledInfo(!showDisabledInfo)
        }
      >
        <BodySmallText>{t('product', 'transparency.store-info')}</BodySmallText>
      </Popover>
    </>
  );
};
